import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { socket } from '../../lib/socket';
import { useToasts } from '../../lib/toast';
import { useLang } from '../../lib/lang';
import type { ReservationRow } from '@taj/types';

const frDate = (iso: string) => {
  const [y, m, d] = (iso || '').split('-');
  return d ? `${d}/${m}/${y}` : iso;
};

const STATUS: Record<string, { ic: string; label: [string, string]; color: string }> = {
  PENDING: { ic: '⏳', label: ['En attente', 'Pending'], color: '#c8941e' },
  CONFIRMED: { ic: '✅', label: ['Confirmée', 'Confirmed'], color: '#1f9150' },
  DECLINED: { ic: '✕', label: ['Refusée', 'Declined'], color: '#c0392b' },
  CANCELLED: { ic: '—', label: ['Annulée', 'Cancelled'], color: '#9a8c70' },
};

export function ReservationsManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const lang = useLang((s) => s.lang);
  const t = (fr: string, en: string) => (lang === 'fr' ? fr : en);
  const [rows, setRows] = useState<ReservationRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: '', phone: '', email: '', date: '', time: '', partySize: 2, note: '' });
  const setField = (k: keyof typeof f, v: string | number) => setF((p) => ({ ...p, [k]: v }));

  const now = new Date();
  const minDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const quick = (days: number) => { const s = new Date(); s.setDate(s.getDate() - days); setFrom(isoDay(s)); setTo(isoDay(new Date())); };

  const load = useCallback(() => {
    api.listReservations(from || undefined, to || undefined).then(setRows).catch(() => {});
  }, [from, to]);

  useEffect(() => {
    load();
    const onNew = (r: ReservationRow) => setRows((prev) => [r, ...prev.filter((x) => x.id !== r.id)]);
    socket.on('reservation:new', onNew);
    return () => { socket.off('reservation:new', onNew); };
  }, [load]);

  const act = async (id: string, action: 'confirm' | 'decline', reason?: string) => {
    setBusy(id);
    try {
      const updated = action === 'confirm' ? await api.confirmReservation(id) : await api.declineReservation(id, reason);
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
      push(action === 'confirm' ? t('✅ Réservation confirmée', '✅ Confirmed') : t('Réservation refusée', 'Declined'));
    } catch (e: any) {
      push(e.message, 'warn');
    } finally {
      setBusy(null);
    }
  };

  const createResa = async () => {
    if (f.name.trim().length < 2 || f.phone.replace(/\D/g, '').length < 6 || !f.date || !f.time) {
      return push(t('Nom, téléphone, date et heure requis', 'Name, phone, date and time required'), 'warn');
    }
    setSaving(true);
    try {
      await api.createReservation({
        name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim() || undefined,
        date: f.date, time: f.time, partySize: f.partySize, note: f.note.trim() || undefined,
      });
      push(t('✅ Réservation enregistrée', '✅ Reservation saved'));
      setF({ name: '', phone: '', email: '', date: '', time: '', partySize: 2, note: '' });
      setShowNew(false);
      load();
    } catch (e: any) {
      push(e.message, 'warn');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box' as const, border: '1px solid #e8ddc6', borderRadius: 10, padding: '10px 12px', fontSize: '1rem', background: '#fff', fontFamily: 'inherit' };
  const qbtn = { border: '1px solid #e8ddc6', background: '#f6efe1', borderRadius: 8, padding: '6px 10px', fontWeight: 700, cursor: 'pointer' as const, fontSize: '.85rem' };

  const pending = rows.filter((r) => r.status === 'PENDING');
  const treated = rows.filter((r) => r.status !== 'PENDING');

  const card = (r: ReservationRow) => {
    const st = STATUS[r.status] ?? STATUS.PENDING;
    const isPending = r.status === 'PENDING';
    return (
      <div key={r.id} style={{ background: isPending ? '#fff3d6' : '#fffdf8', border: `1px solid ${isPending ? '#e0a828' : '#e8ddc6'}`, borderLeft: `6px solid ${st.color}`, boxShadow: isPending ? '0 2px 10px rgba(224,168,40,.25)' : 'none', borderRadius: 14, padding: 14, marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#241c10' }}>👤 {r.name} · {r.partySize} {t('pers.', 'guests')}</div>
          <div style={{ fontWeight: 800, color: st.color }}>{st.ic} {t(st.label[0], st.label[1])}</div>
        </div>
        <div style={{ fontSize: '1.05rem', color: '#241c10', margin: '4px 0', fontWeight: 700 }}>
          🗓️ {frDate(r.date)} · ⏰ {r.time}
        </div>
        <div style={{ fontSize: '.9rem', color: '#5f5642' }}>
          📞 <a href={`tel:${r.phone}`} style={{ color: '#2a6fb0' }}>{r.phone}</a>
          {r.email ? <> · 📧 {r.email}</> : null}
        </div>
        {r.note && <div style={{ fontSize: '.88rem', color: '#5f5642', marginTop: 4, fontStyle: 'italic' }}>💬 {r.note}</div>}
        {r.status === 'DECLINED' && r.declineReason && <div style={{ fontSize: '.85rem', color: '#c0392b', marginTop: 4 }}>✕ {t('Motif du refus', 'Decline reason')} : {r.declineReason}</div>}
        {r.status === 'PENDING' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              disabled={busy === r.id}
              onClick={() => act(r.id, 'confirm')}
              style={{ flex: 1, background: '#1f9150', color: '#fff', border: 'none', borderRadius: 10, padding: '10px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' }}
            >✓ {t('Accepter', 'Accept')}</button>
            <button
              disabled={busy === r.id}
              onClick={() => { const reason = prompt(t('Motif du refus (envoyé au client par email) :', 'Reason for declining (emailed to the customer):'), ''); if (reason !== null) act(r.id, 'decline', reason); }}
              style={{ flex: 1, background: '#fff', color: '#c0392b', border: '1px solid #e8c6c0', borderRadius: 10, padding: '10px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' }}
            >✕ {t('Refuser', 'Decline')}</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onBack} style={{ background: '#fff', border: '1px solid #e8ddc6', borderRadius: 10, padding: '8px 14px', fontWeight: 800, cursor: 'pointer', color: '#7a1f2b' }}>← {t('Retour caisse', 'Back')}</button>
        <h2 style={{ margin: 0, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '.06em', color: '#7a1f2b', fontSize: '1.9rem' }}>📅 {t('Réservations', 'Reservations')}</h2>
        <button onClick={() => setShowNew((v) => !v)} style={{ marginLeft: 'auto', background: '#1f7a6f', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 14px', fontWeight: 800, cursor: 'pointer' }}>{showNew ? `✕ ${t('Fermer', 'Close')}` : `➕ ${t('Nouvelle', 'New')}`}</button>
        <button onClick={load} title={t('Rafraîchir', 'Refresh')} style={{ background: '#fff', border: '1px solid #e8ddc6', borderRadius: 10, padding: '8px 12px', cursor: 'pointer' }}>🔄</button>
      </div>

      {showNew && (
        <div style={{ background: '#fffdf8', border: '1px solid #e8ddc6', borderRadius: 14, padding: 16, marginBottom: 18 }}>
          <div style={{ fontWeight: 800, color: '#7a1f2b', marginBottom: 10 }}>➕ {t('Réservation par téléphone', 'Phone reservation')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input style={inputStyle} placeholder={t('Nom', 'Name')} value={f.name} onChange={(e) => setField('name', e.target.value)} />
            <input style={inputStyle} placeholder={t('Téléphone', 'Phone')} inputMode="tel" value={f.phone} onChange={(e) => setField('phone', e.target.value)} />
            <input style={inputStyle} type="date" min={minDate} value={f.date} onChange={(e) => setField('date', e.target.value)} />
            <input style={inputStyle} type="time" value={f.time} onChange={(e) => setField('time', e.target.value)} />
            <input style={inputStyle} placeholder={t('Email (facultatif)', 'Email (optional)')} inputMode="email" value={f.email} onChange={(e) => setField('email', e.target.value)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
              <span style={{ color: '#5f5642', fontWeight: 700 }}>👥</span>
              <button type="button" onClick={() => setField('partySize', Math.max(1, f.partySize - 1))} style={{ width: 40, height: 40, borderRadius: 10, border: '1px solid #e8ddc6', background: '#fff', fontSize: '1.3rem', cursor: 'pointer' }}>−</button>
              <b style={{ fontSize: '1.3rem', minWidth: 26, textAlign: 'center' }}>{f.partySize}</b>
              <button type="button" onClick={() => setField('partySize', Math.min(50, f.partySize + 1))} style={{ width: 40, height: 40, borderRadius: 10, border: '1px solid #e8ddc6', background: '#fff', fontSize: '1.3rem', cursor: 'pointer' }}>+</button>
            </div>
          </div>
          <input style={{ ...inputStyle, marginTop: 10 }} placeholder={t('Message (optionnel)', 'Message (optional)')} value={f.note} onChange={(e) => setField('note', e.target.value)} />
          <button disabled={saving} onClick={createResa} style={{ width: '100%', marginTop: 12, background: '#1f9150', color: '#fff', border: 'none', borderRadius: 12, padding: 13, fontWeight: 800, fontSize: '1.05rem', cursor: 'pointer' }}>
            {saving ? t('Enregistrement…', 'Saving…') : `✓ ${t('Enregistrer la réservation', 'Save reservation')}`}
          </button>
        </div>
      )}

      <div style={{ background: '#fffdf8', border: '1px solid #e8ddc6', borderRadius: 14, padding: 12, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, color: '#7a1f2b', marginBottom: 8 }}>🔎 {t('Historique par date', 'History by date')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: '.85rem', color: '#5f5642', display: 'flex', alignItems: 'center', gap: 4 }}>{t('Du', 'From')}
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ border: '1px solid #e8ddc6', borderRadius: 8, padding: '6px 8px', background: '#fff' }} />
          </label>
          <label style={{ fontSize: '.85rem', color: '#5f5642', display: 'flex', alignItems: 'center', gap: 4 }}>{t('Au', 'To')}
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ border: '1px solid #e8ddc6', borderRadius: 8, padding: '6px 8px', background: '#fff' }} />
          </label>
          <button onClick={() => quick(7)} style={qbtn}>7 j</button>
          <button onClick={() => quick(30)} style={qbtn}>30 j</button>
          <button onClick={() => quick(90)} style={qbtn}>3 {t('mois', 'months')}</button>
          <button onClick={() => { setFrom('2000-01-01'); setTo('2100-01-01'); }} style={qbtn}>{t('Tout', 'All')}</button>
          {(from || to) && <button onClick={() => { setFrom(''); setTo(''); }} style={{ ...qbtn, color: '#c0392b' }}>✕ {t('Effacer', 'Clear')}</button>}
        </div>
        {(from || to) && <div style={{ fontSize: '.82rem', color: '#5f5642', marginTop: 8 }}>{rows.length} {t('réservation(s) sur la période', 'reservation(s) in range')}</div>}
      </div>

      <h3 style={{ color: '#c8941e', margin: '0 0 8px' }}>⏳ {t('En attente', 'Pending')} ({pending.length})</h3>
      {pending.length === 0
        ? <div style={{ color: '#9a8c70', marginBottom: 20 }}>{t('Aucune réservation en attente.', 'No pending reservations.')}</div>
        : pending.map(card)}

      <h3 style={{ color: '#5f5642', margin: '18px 0 8px' }}>📖 {t('Traitées', 'Handled')} ({treated.length})</h3>
      {treated.length === 0
        ? <div style={{ color: '#9a8c70' }}>{t('Aucune.', 'None.')}</div>
        : treated.map(card)}
    </div>
  );
}
